import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AtSign, Reply, FileCode2, Eye, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detectVars(...sources) {
  const set = new Set();
  const re = /\{\{\s*([\w.-]+)\s*\}\}/g;
  sources.forEach((s) => { let m; while ((m = re.exec(s || ""))) set.add(m[1]); });
  return [...set];
}

export default function ContentStep({ data, onChange, firstRecipientVars }) {
  const [domains, setDomains] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [html, setHtml] = useState("");
  const [errors, setErrors] = useState([]);
  const [previewVars, setPreviewVars] = useState({});
  const debounce = useRef();

  const localPart = data.fromLocal || "";
  const fromDomain = data.fromDomain || "";
  const fromEmail = localPart && fromDomain ? `${localPart}@${fromDomain}` : "";
  const fromName = data.fromName || "";
  const subject = data.subject || "";
  const mjml = data.mjml || "";

  useEffect(() => {
    axios.get(`${API}/v1/settings/domains`).then((r) => setDomains(r.data));
    axios.get(`${API}/v1/templates`).then((r) => setTemplates(r.data));
  }, []);

  // Seed preview variables from the first CSV recipient (spec requirement).
  useEffect(() => { if (firstRecipientVars) setPreviewVars((p) => ({ ...firstRecipientVars, ...p })); }, [firstRecipientVars]);

  const vars = useMemo(() => detectVars(mjml, fromName, subject), [mjml, fromName, subject]);

  const contentType = data.contentType || "mjml";

  useEffect(() => {
    if (!mjml) { setHtml(""); setErrors([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data: r } = await axios.post(`${API}/v1/templates/render`, { mjml, variables: previewVars, type: contentType });
        setHtml(r.html || ""); setErrors(r.errors || []);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(debounce.current);
  }, [mjml, previewVars, contentType]);

  const selectTemplate = (t) => onChange({ templateId: t.id, mjml: t.mjml, templateName: t.name, contentType: t.contentType || "mjml" });
  const set = (patch) => onChange(patch);

  const HTML_STARTER = '<div style="font-family:Helvetica,Arial,sans-serif;max-width:600px;margin:0 auto;background:#fff;padding:28px;color:#111827"><h1 style="font-size:22px">Hello {{first_name}}</h1><p style="color:#374151;line-height:1.6">Paste your HTML here — sent as-is.</p></div>';
  const MJML_STARTER = '<mjml><mj-body background-color="#f2f4f6"><mj-section background-color="#ffffff" padding="28px"><mj-column><mj-text font-size="20px" color="#111827">Hello {{first_name}}</mj-text><mj-text color="#374151">Your message here.</mj-text></mj-column></mj-section></mj-body></mjml>';
  const switchType = (next) => {
    if (next === contentType) return;
    const isMjml = mjml.includes("<mjml");
    let nextMjml = mjml;
    if (next === "html" && isMjml) nextMjml = HTML_STARTER;
    if (next === "mjml" && !isMjml) nextMjml = MJML_STARTER;
    onChange({ contentType: next, mjml: nextMjml, templateId: null });
  };

  const nameLen = fromName.length;
  const nameColor = nameLen >= 35 ? "var(--red)" : nameLen >= 25 ? "var(--amber)" : "var(--txt-faint)";
  const applyTag = (s) => (s || "").replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (m, k) => (previewVars[k] != null ? previewVars[k] : m));

  return (
    <div data-testid="content-step">
      <div className="panel"><div className="p-head"><AtSign size={15} /><h3>Sender identity</h3></div>
        <div className="p-body">
          <div className="row3">
            <div className="field"><label>From mailbox</label><input data-testid="from-local-input" value={localPart} onChange={(e) => set({ fromLocal: e.target.value })} placeholder="noreply" /></div>
            <div className="field"><label>Verified domain</label>
              <select data-testid="from-domain-select" value={fromDomain} onChange={(e) => set({ fromDomain: e.target.value })}>
                <option value="">Select domain…</option>
                {domains.map((d) => <option key={d.id} value={d.domain}>{d.domain} {d.verificationStatus === "VALID" ? "✓ verified" : "⚠ unverified"}</option>)}
              </select>
            </div>
            <div className="field"><label>Reply-To (optional)</label><input data-testid="replyto-input" value={data.replyTo || ""} onChange={(e) => set({ replyTo: e.target.value })} placeholder={fromEmail || "defaults to From"} /></div>
          </div>
          <div className="row3">
            <div className="field"><label>From name <span style={{ color: nameColor }} data-testid="fromname-counter">({nameLen}/35)</span></label>
              <input data-testid="from-name-input" value={fromName} onChange={(e) => set({ fromName: e.target.value })} placeholder="{{company}} Team" />
            </div>
            <div className="field" style={{ gridColumn: "span 2" }}><label>Subject</label><input data-testid="subject-input" value={subject} onChange={(e) => set({ subject: e.target.value })} placeholder="Hi {{first_name}}, a quick update" /></div>
          </div>
          <p className="note"><Reply size={11} /> Replies route to Reply-To (or From). Use a monitored inbox for B2B.</p>
          <div className="merge-tag-hint" data-testid="merge-tag-hint">
            <span className="mth-label">Available tags:</span>
            {["first_name", "recipient_company"].map((t) => (
              <code key={t} className="mth-chip" title={`Auto-populated for every recipient`}>{"{{" + t + "}}"}</code>
            ))}
            <span className="mth-note">+ any column you mapped from your CSV</span>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}><div className="p-head"><FileCode2 size={15} /><h3>Choose a template</h3><span className="hint">{templates.length} saved</span>
        <div className="type-toggle" data-testid="content-type-toggle" style={{ marginLeft: "auto" }}>
          <button className={`tt-btn ${contentType === "mjml" ? "active" : ""}`} data-testid="campaign-type-mjml" onClick={() => switchType("mjml")}>MJML</button>
          <button className={`tt-btn ${contentType === "html" ? "active" : ""}`} data-testid="campaign-type-html" onClick={() => switchType("html")}>HTML</button>
        </div>
      </div>
        <div className="p-body">
          <div className="tpl-cards" data-testid="template-grid">
            {templates.length === 0 && <p className="note">No templates yet — create one in the Templates tab.</p>}
            {templates.map((t) => (
              <button key={t.id} className={`tpl-card ${data.templateId === t.id ? "sel" : ""}`} data-testid={`template-card-${t.name}`} onClick={() => selectTemplate(t)}>
                <FileCode2 size={16} /><span className="tc-name">{t.name}</span>
                {(t.variables || []).slice(0, 3).map((v) => <span key={v} className="chip-tag">{v}</span>)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {mjml && (
        <>
          {vars.length > 0 && (
            <div className="panel" style={{ marginTop: 16 }}><div className="p-head"><Eye size={15} /><h3>Preview variables</h3><span className="hint">seeded from first recipient</span></div>
              <div className="p-body"><div className="row3">
                {vars.map((k) => <div className="field" key={k}><label>{`{{${k}}}`}</label><input data-testid={`preview-var-${k}`} value={previewVars[k] ?? ""} onChange={(e) => setPreviewVars({ ...previewVars, [k]: e.target.value })} /></div>)}
              </div></div>
            </div>
          )}
          <div className="tpl-split" style={{ marginTop: 16 }}>
            <div className="panel tpl-editor-panel"><div className="p-head"><FileCode2 size={15} /><h3>{contentType === "html" ? "HTML" : "MJML"}</h3></div>
              <textarea data-testid="content-mjml" className="mjml-code" spellCheck={false} value={mjml} onChange={(e) => set({ mjml: e.target.value, templateId: null })} />
              {errors.length > 0 && <div className="tpl-errors" data-testid="content-mjml-errors">{errors.map((e, i) => <div key={i} className="tpl-err"><AlertTriangle size={12} /> Line {e.line} — {e.message}</div>)}</div>}
            </div>
            <div className="panel tpl-preview-panel"><div className="p-head"><Eye size={15} /><h3>Preview</h3></div>
              <div className="preview-from" data-testid="preview-sender-header">
                <div className="pf-avatar">{(applyTag(fromName)[0] || "?").toUpperCase()}</div>
                <div><div className="pf-name">{applyTag(fromName) || "From name"}</div><div className="pf-email">{fromEmail || "no From email"}</div></div>
                <div className="pf-subj">{applyTag(subject) || "(no subject)"}</div>
              </div>
              <iframe data-testid="content-preview" title="preview" className="mjml-frame" srcDoc={html} sandbox="" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
